import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuItem {
  /** Sichtbarer Label-Text. */
  label: string;
  /** Optional Icon links neben dem Label. */
  icon?: React.ReactNode;
  /** Submenu öffnen statt onSelect. */
  submenu?: ContextMenuItem[];
  /** Klick-Aktion (wird nicht aufgerufen wenn `submenu` gesetzt). */
  onSelect?: () => void | Promise<void>;
  /** Visuell deaktivieren. */
  disabled?: boolean;
  /** Markiert die Action als destruktiv (rot). */
  destructive?: boolean;
  /** Trennlinie ÜBER diesem Item einfügen. */
  separator?: boolean;
}

interface Props {
  /** Bildschirm-Koordinaten (clientX/Y). */
  position: { x: number; y: number };
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * Generisches Rechtsklick-Kontextmenü. Wird per Portal direkt unter
 * `document.body` gerendert und schließt bei Klick außerhalb, Esc oder
 * nach Auswahl.
 *
 * Submenüs werden als zweite Spalte rechts neben dem Hover-Item geöffnet.
 */
export function ContextMenu({ position, items, onClose }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Clamp an Viewport, damit das Menü nicht aus dem Bildschirm läuft.
  const left = Math.min(position.x, window.innerWidth - 220);
  const top = Math.min(position.y, window.innerHeight - 32 - items.length * 32);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-50 min-w-[210px] rounded-card border border-border bg-surface shadow-lg shadow-black/40 py-1 text-text"
      style={{ left, top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, idx) => (
        <Item key={idx} item={item} onSelect={onClose} />
      ))}
    </div>,
    document.body,
  );
}

function Item({
  item,
  onSelect,
}: {
  item: ContextMenuItem;
  onSelect: () => void;
}): JSX.Element {
  const sub = item.submenu;
  return (
    <>
      {item.separator ? <div className="my-1 border-t border-border" /> : null}
      <div className="relative group">
        <button
          type="button"
          disabled={item.disabled}
          onClick={() => {
            if (item.disabled || sub) return;
            void item.onSelect?.();
            onSelect();
          }}
          className={[
            'w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3',
            'hover:bg-hover transition',
            item.disabled ? 'opacity-40 cursor-not-allowed' : '',
            item.destructive ? 'text-err hover:bg-[#3a1e1e]' : '',
          ].join(' ')}
        >
          <span className="flex items-center gap-2.5">
            {item.icon ? <span className="w-3.5 h-3.5 flex items-center justify-center">{item.icon}</span> : null}
            {item.label}
          </span>
          {sub ? <span className="text-text-mute text-xxs">▸</span> : null}
        </button>
        {sub ? (
          <div className="hidden group-hover:block absolute left-full top-0 -ml-px min-w-[200px] rounded-card border border-border bg-surface shadow-lg shadow-black/40 py-1">
            {sub.map((s, i) => (
              <Item key={i} item={s} onSelect={onSelect} />
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
